import React, { useState } from 'react';

// Reference categorical palette (fixed order, never cycled); slot 8 is reserved for "Other".
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const OTHER = '#8a8984';
const MAX_SLICES = SERIES.length;

const polar = (cx, cy, r, angle) => [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];

const arcPath = (cx, cy, outer, inner, start, end) => {
  const large = end - start > Math.PI ? 1 : 0;
  const [x1, y1] = polar(cx, cy, outer, start);
  const [x2, y2] = polar(cx, cy, outer, end);
  const [x3, y3] = polar(cx, cy, inner, end);
  const [x4, y4] = polar(cx, cy, inner, start);
  return `M${x1} ${y1} A${outer} ${outer} 0 ${large} 1 ${x2} ${y2} L${x3} ${y3} A${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`;
};

/**
 * Donut with a legend that doubles as the data table. slices: [{ key, label, count, onSelect?, selected? }].
 * Slices beyond the palette fold into a non-clickable "Other".
 */
const DonutChart = ({ title, slices = [], emptyText = 'No data yet', footnote }) => {
  const [active, setActive] = useState(null);
  const sorted = [...slices].filter((s) => s.count > 0).sort((a, b) => b.count - a.count);
  const head = sorted.slice(0, MAX_SLICES);
  const rest = sorted.slice(MAX_SLICES);
  const shown = rest.length
    ? [...head, { key: '__other', label: `Other (${rest.length})`, count: rest.reduce((sum, s) => sum + s.count, 0) }]
    : head;
  const total = shown.reduce((sum, s) => sum + s.count, 0);

  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  let angle = -Math.PI / 2;
  const gap = shown.length > 1 ? 0.02 : 0;
  const arcs = shown.map((slice, index) => {
    const sweep = (slice.count / total) * Math.PI * 2;
    const start = angle + gap / 2;
    const end = angle + sweep - gap / 2;
    angle += sweep;
    return { ...slice, color: slice.key === '__other' ? OTHER : SERIES[index], start, end };
  });
  const highlighted = arcs.find((a) => a.key === active);

  return (
    <div className="rounded-lg border bg-white p-4 flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {total === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">{emptyText}</p>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={title} className="flex-shrink-0">
            {arcs.length === 1 ? (
              <circle cx={cx} cy={cy} r={62} fill="none" stroke={arcs[0].color} strokeWidth={28} />
            ) : (
              arcs.map((arc) => (
                <path
                  key={arc.key}
                  d={arcPath(cx, cy, 76, 48, arc.start, arc.end)}
                  fill={arc.color}
                  opacity={active && active !== arc.key ? 0.35 : 1}
                  stroke={arc.selected ? '#111827' : undefined}
                  strokeWidth={arc.selected ? 2 : undefined}
                  className={arc.onSelect ? 'cursor-pointer' : ''}
                  onMouseEnter={() => setActive(arc.key)}
                  onMouseLeave={() => setActive(null)}
                  onClick={arc.onSelect}
                >
                  <title>{`${arc.label}: ${arc.count.toLocaleString()} (${Math.round((arc.count / total) * 100)}%)`}</title>
                </path>
              ))
            )}
            <text x={cx} y={cy - 2} textAnchor="middle" className="fill-gray-900" fontSize="20" fontWeight="600">
              {(highlighted ? highlighted.count : total).toLocaleString()}
            </text>
            <text x={cx} y={cy + 16} textAnchor="middle" className="fill-gray-500" fontSize="10">
              {highlighted ? `${Math.round((highlighted.count / total) * 100)}%` : 'total'}
            </text>
          </svg>
          <ul className="w-full space-y-1 text-sm">
            {arcs.map((arc) => {
              const content = (
                <>
                  <span className="h-3 w-3 rounded-sm flex-shrink-0" style={{ backgroundColor: arc.color }} />
                  <span className="flex-1 truncate text-left text-gray-700">{arc.label}</span>
                  <span className="tabular-nums text-gray-900">{arc.count.toLocaleString()}</span>
                  <span className="w-10 text-right tabular-nums text-gray-500">{Math.round((arc.count / total) * 100)}%</span>
                </>
              );
              return (
                <li key={arc.key} onMouseEnter={() => setActive(arc.key)} onMouseLeave={() => setActive(null)}>
                  {arc.onSelect ? (
                    <button
                      type="button"
                      onClick={arc.onSelect}
                      aria-pressed={!!arc.selected}
                      className={`w-full flex items-center gap-2 rounded px-1 py-0.5 ${arc.selected ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50'}`}
                    >
                      {content}
                    </button>
                  ) : (
                    <div className="w-full flex items-center gap-2 px-1 py-0.5">{content}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {footnote && <p className="text-xs text-gray-500">{footnote}</p>}
    </div>
  );
};

export default DonutChart;
